
/**
 * Centralized Environment Variables Configuration
 *
 * This file provides type-safe access to all environment variables.
 * In Lovable production, these are managed through the platform's environment settings.
 * In local development, these come from .env.local file.
 *
 * Usage:
 * import { config } from '@/config/env';
 * const response = await fetch(`${config.backendBaseUrl}/endpoint`);
 */

interface EnvironmentConfig {
  // Backend API
  backendBaseUrl: string;

  // Azure Functions (URL is public, key is stored securely in Lovable Cloud)
  azureFunctionsUrl: string;
  azureFunctionsKey: string;

  // Environment
  env: "development" | "production" | "test";
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Validates and returns environment variables with fallbacks
 */
function getConfig(): EnvironmentConfig {
  const backendBaseUrl =
    import.meta.env.VITE_BACKEND_BASE_URL ||
    "https://dev.smartsundae.com/api/v2/";
  const azureFunctionsUrl =
    import.meta.env.VITE_AZURE_FUNCTIONS_URL ||
    "https://meeting-gc-functions-v2.azurewebsites.net/api";
  const azureFunctionsKey = import.meta.env.VITE_AZURE_FUNCTIONS_KEY || "";
  const env = (import.meta.env.VITE_ENV ||
    import.meta.env.MODE ||
    "development") as "development" | "production" | "test";

  // Validation: Critical variables must be present
  if (!backendBaseUrl) {
    console.error("❌ VITE_BACKEND_BASE_URL is not defined");
  }

  if (!azureFunctionsUrl) {
    console.error("❌ VITE_AZURE_FUNCTIONS_URL is not defined");
  }

  if (!azureFunctionsKey) {
    console.error("❌ VITE_AZURE_FUNCTIONS_KEY is not defined");
  }

  return {
    backendBaseUrl,
    azureFunctionsUrl,
    azureFunctionsKey,
    env,
    isDevelopment: env === "development",
    isProduction: env === "production",
  };
}

/**
 * Exported configuration object
 * Use this throughout the application instead of import.meta.env
 */
export const config = getConfig();

/**
 * Type-safe access to config
 */
export type { EnvironmentConfig };

// Log configuration in development (helps debugging)
if (config.isDevelopment) {
  console.log("🔧 Environment Configuration:", {
    backendBaseUrl: config.backendBaseUrl,
    azureFunctionsUrl: config.azureFunctionsUrl,
    env: config.env,
    note: "Azure Functions Key stored securely via environment variables",
  });
}
